import { CategoryBoundingBox } from '../../../models/Product';
import useAppDispatch from '../../../hooks/useAppDispatch';
import IconButton from '../../button/IconButton';
import './ProductBoundingBoxMiniature.less';
import { useTranslation } from '../../../hooks/useTranslation';

type ProductBoundingBoxMiniatureProps = {
    size: 'x-small' | 'large';
    imageUrl: string;
    boundingBoxes?: CategoryBoundingBox[];
    selectedBoundingBox?: CategoryBoundingBox;
    imageWidth?: number;
    imageHeight?: number;
};

const sizes: { [size in ProductBoundingBoxMiniatureProps['size']]: number } = {
    'x-small': 32,
    large: 120
};

const ProductBoundingBoxMiniature = ({
    size,
    imageUrl,
    boundingBoxes = [],
    selectedBoundingBox,
    imageWidth,
    imageHeight
}: ProductBoundingBoxMiniatureProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const boxSize = sizes[size];
    const zoomStyles =
        selectedBoundingBox &&
        imageWidth &&
        imageHeight &&
        selectedBoundingBox.width > 0 &&
        selectedBoundingBox.height > 0
            ? (() => {
                  // Scale so the whole image is visible AND the bounding box fits inside the square.
                  // We scale by the factor that fits the *largest* dimension of the image into the container,
                  // then center on the bounding box.
                  const scale = Math.min(
                      boxSize / selectedBoundingBox.width,
                      boxSize / selectedBoundingBox.height
                  );

                  const bgWidth = imageWidth * scale;
                  const bgHeight = imageHeight * scale;

                  // Bounding box center in original image
                  const centerX =
                      selectedBoundingBox.left + selectedBoundingBox.width / 2;
                  const centerY =
                      selectedBoundingBox.top + selectedBoundingBox.height / 2;

                  // Desired offsets so bbox center aligns with container center
                  let posX = -(centerX * scale - boxSize / 2);
                  let posY = -(centerY * scale - boxSize / 2);

                  // Clamp so image is not cropped (keeps full image in view)
                  const minX = boxSize - bgWidth;
                  const minY = boxSize - bgHeight;
                  if (posX > 0) posX = 0;
                  if (posY > 0) posY = 0;
                  if (posX < minX) posX = minX;
                  if (posY < minY) posY = minY;

                  return {
                      backgroundSize: `${bgWidth}px ${bgHeight}px`,
                      backgroundPosition: `${posX}px ${posY}px`,
                      backgroundRepeat: 'no-repeat'
                  } as const;
              })()
            : {};

    return (
        <div
            className={`bri-ProductBoundingBoxMiniature bri-ProductBoundingBoxMiniature-size-${size}`}
            style={{
                backgroundImage: `url(${imageUrl})`,
                '--product-bounding-box-miniature-size': `${sizes[size]}px`,
                ...zoomStyles
            }}
        >
            {boundingBoxes.length > 0 && (
                <IconButton
                    iconId="clothes"
                    size="small"
                    variant="glass"
                    title={t('PRODUCT_SEARCH_BY_IMAGE_SELECT_ANOTHER_RESULT')}
                    onClick={() =>
                        dispatch.featureState.changeSelectedBoundingBox()
                    }
                />
            )}
        </div>
    );
};
export default ProductBoundingBoxMiniature;
